/**
 * useAIResolveAnalyze — Calls POST /ai/resolve/analyze and manages FixCard state.
 *
 * Flow:
 *   1. Call /ai/resolve/analyze with file S3 URI + errors
 *   2. Receive FixCards: plain-English explanation + one proposed fix per error
 *   3. User clicks "Apply Fix" → applyFix(card) patches the line + saves overlay + reruns
 *   4. If afterErrors > beforeErrors → auto-rollback
 *   5. User clicks "Fix It Myself" → openEditor(card) navigates to Fix & Continue at that line
 *
 * Returns:
 *   { analyzeState, startAnalyze, applyFix, openEditor, dismiss }
 */

import { useState, useCallback, useRef } from 'react'
import { getAPIBaseUrl } from '../environment'
import { getAuthHeaders } from '../services/nodeExecutors/authHelper'

// States
const STATE_IDLE       = 'idle'
const STATE_LOADING    = 'loading'
const STATE_READY      = 'ready'
const STATE_APPLYING   = 'applying'
const STATE_APPLIED    = 'applied'
const STATE_ERROR      = 'error'

const INITIAL_STATE = {
  phase:      STATE_IDLE,
  cards:      [],
  message:    '',
  error:      null,
  applying:   null,    // card currently being applied
  applied:    [],      // card ids that have been successfully applied
  rolled_back: [],     // card ids that were rolled back
}

export function useAIResolveAnalyze(analysisContext, apiBase, onRerunComplete) {
  const [analyzeState, setAnalyzeState] = useState(INITIAL_STATE)
  const abortRef = useRef(null)

  // ── Helpers ──────────────────────────────────────────────────────────────

  const _setPhase = (phase, extras = {}) =>
    setAnalyzeState(prev => ({ ...prev, phase, ...extras }))

  const _cardKey = (card) => `${card.file}:${card.line}:${card.error_message || ''}`

  // ── startAnalyze — call /ai/resolve/analyze ───────────────────────────────

  const startAnalyze = useCallback(async (preFlightJobId, filename, errors) => {
    if (!preFlightJobId || !filename || !errors?.length) return

    setAnalyzeState({ ...INITIAL_STATE, phase: STATE_LOADING })

    try {
      const base = apiBase || getAPIBaseUrl()
      const headers = await getAuthHeaders()

      const body = {
        scout_account_id: analysisContext?.scout_account_id,
        user_id:          analysisContext?.user_id,
        pre_flight_job_id: preFlightJobId,
        filename:          filename,
        errors:           errors.map(e => ({
          file:    e.javaLocation?.file || e.file || 'unknown.java',
          line:    e.javaLocation?.line || e.line || 0,
          message: e.summary || e.message || '',
        })),
      }

      const resp = await fetch(`${base}/ai/resolve/analyze`, {
        method:  'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body:    JSON.stringify(body),
      })

      if (!resp.ok) {
        const err = await resp.text()
        throw new Error(`Server error ${resp.status}: ${err}`)
      }

      const data = await resp.json()

      setAnalyzeState(prev => ({
        ...prev,
        phase:   STATE_READY,
        cards:   data.fix_cards || [],
        message: data.message   || '',
        error:   null,
      }))
    } catch (err) {
      setAnalyzeState(prev => ({
        ...prev,
        phase: STATE_ERROR,
        error: err.message,
      }))
    }
  }, [analysisContext, apiBase])

  // ── applyFix — call server-side /ai/resolve/apply (save overlay only) ────

  const applyFix = useCallback(async (card, preFlightJobId) => {
    if (!card.can_auto_fix || !card.fix) return

    const key = _cardKey(card)
    _setPhase(STATE_APPLYING, { applying: key })

    try {
      const base    = apiBase || getAPIBaseUrl()
      const headers = await getAuthHeaders()

      // Server-side: fetch file, patch line, save overlay
      const resp = await fetch(`${base}/ai/resolve/apply`, {
        method:  'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          scout_account_id:  analysisContext?.scout_account_id,
          user_id:           analysisContext?.user_id,
          pre_flight_job_id: preFlightJobId,
          application_name:  analysisContext?.application_name,
          filename:          card.file,
          fix_line:          card.fix.line,
          old_text:          card.fix.old_text,
          new_text:          card.fix.new_text,
        }),
      })

      if (!resp.ok) {
        const err = await resp.text()
        throw new Error(`Apply failed: ${err}`)
      }

      // Mark card as applied — no auto-rerun
      setAnalyzeState(prev => ({
        ...prev,
        phase:    STATE_APPLIED,
        applying: null,
        applied:  [...(prev.applied || []), key],
        error:    null,
      }))
    } catch (err) {
      setAnalyzeState(prev => ({
        ...prev,
        phase:   STATE_ERROR,
        applying: null,
        error:   err.message,
      }))
    }
  }, [apiBase, analysisContext])

  // ── dismiss ───────────────────────────────────────────────────────────────

  const dismiss = useCallback(() => {
    setAnalyzeState(INITIAL_STATE)
  }, [])

  return {
    analyzeState,
    startAnalyze,
    applyFix,
    dismiss,
    STATE_IDLE,
    STATE_LOADING,
    STATE_READY,
    STATE_APPLYING,
    STATE_APPLIED,
    STATE_ERROR,
  }
}
